import moment from "moment";
import {
    DOWNTIMEMETHODCAPTURE,
    FAILURE_TYPES_CODE_EXCEPTIONS,
    MEASURE_METRICS,
    SPLIT_MIN_EVENT_DURATION_MS,
    SPLIT_MIN_SEGMENT_DURATION_MS,
} from "constants/constants";
import { IEventReportDetails } from "entities/EventReports/EventReportsEntityInterfaces";
import { IShiftScheduleWindow, isEventInCurrentOrPreviousShift } from "utilities/utilities";
import {
    createEmptySegmentFieldValues,
    getSegmentRequiredSatisfied,
    ISegmentFieldValues,
} from "../SegmentFields";
import { ISplitMutationVariables, ISplitSegmentState, ISplitValidationResult } from "./SplitDowntimeModalInterfaces";

const toUtcIso = (value: string | Date | number) => moment.utc(value).toISOString();
const toMs = (value: string | Date) => moment.utc(value).valueOf();

/** Distinct shift schedules the event touched, used to compute split eligibility client-side. */
export const getRowShiftWindows = (row: IEventReportDetails): IShiftScheduleWindow[] => {
    const seenShiftIds = new Set<number>();
    const windows: IShiftScheduleWindow[] = [];

    row.event_report_downtime_details.forEach((detail) => {
        const shiftHistoric = detail.shift_day?.shift_historic;
        const shiftId = shiftHistoric?.shift?.shift_id;
        if (!shiftHistoric || shiftId == null || seenShiftIds.has(shiftId)) return;
        seenShiftIds.add(shiftId);
        windows.push({
            start_time: shiftHistoric.start_time,
            end_time: shiftHistoric.end_time,
            enabledDayNames: shiftHistoric.shift_days.filter((day) => day.is_enabled).map((day) => day.day.name),
        });
    });

    return windows;
};

export type SplitDisabledReason = "ongoing" | "type" | "too_short" | "recent_shift";

/**
 * Pure split-eligibility check used to enable/disable the Split action. Reasons (in
 * order): ongoing (no end_date) → type (below-trigger / not-selected can't be split)
 * → too_short (event not longer than SPLIT_MIN_EVENT_DURATION_MS) → recent_shift (still
 * in the current/previous shift). Fail-safe: missing timezone → recent_shift.
 */
export const getSplitEligibility = (
    row: IEventReportDetails,
    millTimezone: string | undefined,
    now?: moment.Moment,
): { eligible: boolean; reason: SplitDisabledReason | null } => {
    if (!row.end_date) return { eligible: false, reason: "ongoing" };

    const code = row.failure_type.code;
    if (code === FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER || code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED) {
        return { eligible: false, reason: "type" };
    }

    if (toMs(row.end_date) - toMs(row.start_date) <= SPLIT_MIN_EVENT_DURATION_MS) {
        return { eligible: false, reason: "too_short" };
    }

    if (!millTimezone || isEventInCurrentOrPreviousShift(getRowShiftWindows(row), row.end_date, millTimezone, now)) {
        return { eligible: false, reason: "recent_shift" };
    }

    return { eligible: true, reason: null };
};

/** Greatest start among the segments that have a start set (the original always does). */
const getLastStartMs = (segments: ISplitSegmentState[], row: IEventReportDetails): number =>
    Math.max(
        ...segments
            .filter((segment) => segment.isOriginal || segment.start)
            .map((segment) => (segment.isOriginal ? toMs(row.start_date) : toMs(segment.start as string))),
    );

/** Another segment fits only if there is room for it plus its (≥ MIN) tail. */
export const canAddSegment = (segments: ISplitSegmentState[], row: IEventReportDetails): boolean => {
    if (segments.some((segment) => !segment.isOriginal && !segment.start)) return false;
    return getLastStartMs(segments, row) + 2 * SPLIT_MIN_SEGMENT_DURATION_MS <= toMs(row.end_date);
};

/** Pre-fill start for a newly added segment: the start of the next free range. */
export const getNextAvailableStartIso = (segments: ISplitSegmentState[], row: IEventReportDetails): string => {
    const nextMs = Math.min(
        getLastStartMs(segments, row) + SPLIT_MIN_SEGMENT_DURATION_MS,
        toMs(row.end_date) - SPLIT_MIN_SEGMENT_DURATION_MS,
    );
    return toUtcIso(nextMs);
};

/** Allowed [min, max] start for a segment, bounded by its neighbours ± MIN so picks can't overlap. */
export const getSegmentStartBounds = (
    segments: ISplitSegmentState[],
    index: number,
    row: IEventReportDetails,
): { minIso: string; maxIso: string } => {
    const originalStartMs = toMs(row.start_date);
    const originalEndMs = toMs(row.end_date);
    const previous = segments[index - 1];
    const next = segments[index + 1];
    const previousStartMs = previous
        ? previous.isOriginal
            ? originalStartMs
            : previous.start
            ? toMs(previous.start)
            : originalStartMs
        : originalStartMs;
    const nextStartMs = next && next.start ? toMs(next.start) : originalEndMs;
    return {
        minIso: toUtcIso(previousStartMs + SPLIT_MIN_SEGMENT_DURATION_MS),
        maxIso: toUtcIso(nextStartMs - SPLIT_MIN_SEGMENT_DURATION_MS),
    };
};

/** Seeds the modal's first (original) segment from the event row, mirroring the inline-edit seed. */
export const createInitialSegments = (row: IEventReportDetails): ISplitSegmentState[] => {
    const fields: ISegmentFieldValues = {
        failure_type_id: row.failure_type.failure_type_id,
        is_location_specific: row.failure_type.is_location_specific,
        area_id: row.area?.area_id ?? row.area_id ?? undefined,
        functional_location_id: row.functional_location?.functional_location_id ?? undefined,
        failure_code_id: row.failure_code?.failure_code_id ?? undefined,
        user_id: row.user_operator?.user_id ?? undefined,
        notes: row.notes ?? undefined,
    };

    return [
        {
            key: "original",
            isOriginal: true,
            start: toUtcIso(row.start_date),
            failureTypeCode: row.failure_type.code,
            fields,
        },
    ];
};

export const createAddedSegment = (key: string, startIso: string | null = null): ISplitSegmentState => ({
    key,
    isOriginal: false,
    start: startIso,
    failureTypeCode: null,
    fields: createEmptySegmentFieldValues(),
});

/**
 * Validates the segment list. Order-independent: added segments are sorted by start,
 * then every boundary (original start, each segment start, original end) must be at
 * least SPLIT_MIN_SEGMENT_DURATION_MS apart — i.e. every resulting segment is ≥ MIN and
 * within the span. Contiguity/no-gaps is implicit (each segment end = next start via the
 * DB trigger; the last segment keeps the original end).
 */
export const validateSplitSegments = (
    segments: ISplitSegmentState[],
    row: IEventReportDetails,
): ISplitValidationResult => {
    const added = segments.filter((segment) => !segment.isOriginal);
    if (added.length < 1) return { valid: false, errorKey: "min_one_segment" };

    if (added.some((segment) => !segment.start)) return { valid: false, errorKey: "increasing" };

    const originalStart = toMs(row.start_date);
    const originalEnd = toMs(row.end_date);
    const minMs = SPLIT_MIN_SEGMENT_DURATION_MS;

    const sortedStarts = added.map((segment) => toMs(segment.start as string)).sort((a, b) => a - b);
    let previous = originalStart;
    for (const start of sortedStarts) {
        // gap to the previous boundary and the remaining tail must each be ≥ MIN.
        if (start - previous < minMs || start > originalEnd - minMs) return { valid: false, errorKey: "min_duration" };
        previous = start;
    }

    const allFieldsValid = segments.every((segment) =>
        getSegmentRequiredSatisfied(segment.fields, segment.failureTypeCode),
    );
    if (!allFieldsValid) return { valid: false, errorKey: "required_fields" };

    return { valid: true };
};

const buildClassificationInsert = (fields: ISegmentFieldValues, defaultOperatorId?: number) => ({
    failure_type_id: fields.failure_type_id,
    failure_code_id: fields.failure_code_id ?? null,
    ...(fields.is_location_specific
        ? { area_id: fields.area_id ?? null, functional_location_id: fields.functional_location_id ?? null }
        : {}),
    // user_id is NOT NULL on event_report — default to the original event's operator.
    user_id: fields.user_id ?? defaultOperatorId,
    ...(fields.notes ? { notes: fields.notes } : {}),
});

const buildOriginalChanges = (fields: ISegmentFieldValues, editedByUserId?: number) => ({
    failure_type_id: fields.failure_type_id,
    failure_code_id: fields.failure_code_id ?? null,
    // Force location fields null when the type is not location-specific (mirrors inline save).
    ...(fields.is_location_specific
        ? { area_id: fields.area_id ?? null, functional_location_id: fields.functional_location_id ?? null }
        : { area_id: null, functional_location_id: null }),
    ...(fields.user_id != null ? { user_id: fields.user_id } : {}),
    notes: fields.notes ?? null,
    ...(editedByUserId ? { edited_by_user_id: editedByUserId } : {}),
});

/**
 * Builds the split mutation payload: the original event's UPDATE (no end_date — the DB
 * trigger shortens it) and the inserted segments (ascending by start). Each segment
 * carries `splitted_from_id` (the original event id) as the split marker. Middle
 * segments get an explicit `end_date` = the next segment's start; the LAST segment is
 * left without `end_date` so the BEFORE INSERT trigger stamps it from the original's
 * pre-existing successor. The caller spreads `segments` into $seg0..$seg{n-1} for the
 * aliased insert_event_report_one fields.
 */
export const buildSplitMutationVariables = (
    row: IEventReportDetails,
    segments: ISplitSegmentState[],
    editedByUserId?: number,
): ISplitMutationVariables => {
    const original = segments.find((segment) => segment.isOriginal);
    const added = segments
        .filter((segment) => !segment.isOriginal && segment.start)
        .sort((a, b) => toMs(a.start as string) - toMs(b.start as string));

    const defaultOperatorId = row.user_operator?.user_id;

    const segmentsPayload = added.map((segment, index) => {
        const isLast = index === added.length - 1;
        return {
            machine_center_id: row.machine_center_id,
            ...(row.cluster_id != null ? { cluster_id: row.cluster_id } : {}),
            measure_metric_id: MEASURE_METRICS.DOWNTIME,
            downtime_method_capture_id: DOWNTIMEMETHODCAPTURE.MANUAL,
            ignore_event: false,
            splitted_from_id: row.event_report_id,
            start_date: toUtcIso(segment.start as string),
            ...(isLast ? {} : { end_date: toUtcIso(added[index + 1].start as string) }),
            ...buildClassificationInsert(segment.fields, defaultOperatorId),
            ...(editedByUserId ? { edited_by_user_id: editedByUserId } : {}),
        };
    });

    return {
        eventReportId: row.event_report_id,
        originalChanges: original ? buildOriginalChanges(original.fields, editedByUserId) : {},
        segments: segmentsPayload,
    };
};
